import type { CSSProperties, HTMLAttributes } from "react"

import {
  type AssessmentBand,
  dataByAssessmentBand,
} from "@/constants/terminal/assessment-band"
import type { TerminalScoreMovement } from "@/lib/terminal/file-view"
import { cn } from "@/lib/utils"

// The Assessment band's colored mark, color only: pair it with the band's name
// (BandChip, or an sr-only label) so the band is never conveyed by hue alone.
export const BandMark = ({
  band,
  size = "var(--terminal-assessment-mark-size-row)",
  className,
}: {
  band: AssessmentBand
  size?: string
  className?: string
}) => (
  <span
    className={cn("terminal-assessment-mark", className)}
    style={
      {
        "--terminal-assessment-color": dataByAssessmentBand[band].color,
        width: size,
        height: size,
      } as CSSProperties
    }
  />
)

type BandChipSize = "sm" | "md" | "lg"

const chipClassBySize: Record<
  BandChipSize,
  { markSize: string; gap: string; word: string }
> = {
  // The word ladders by context (the band-word-* sizes in the theme): s beside
  // a compact mark, m in legends and the distribution, l beside the masthead
  // square.
  sm: { markSize: "10px", gap: "gap-1.5", word: "band-word-s" },
  md: { markSize: "12px", gap: "gap-2", word: "band-word-m" },
  lg: { markSize: "16px", gap: "gap-2.5", word: "band-word-l" },
}

export const BandChip = ({
  band,
  size = "md",
  className,
  style,
  ...props
}: {
  band: AssessmentBand
  size?: BandChipSize
} & HTMLAttributes<HTMLSpanElement>) => {
  const { label, color } = dataByAssessmentBand[band]
  const { markSize, gap, word } = chipClassBySize[size]
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap",
        gap,
        className
      )}
      style={{ color, ...style }}
      {...props}
    >
      <BandMark band={band} size={markSize} />
      <span
        className={cn(
          "font-display font-semibold tracking-[0.04em] uppercase",
          word
        )}
      >
        {label}
      </span>
    </span>
  )
}

type ScoreSquareSize = "masthead" | "row" | "compact"

// The compact square is too small for a number and stays color only; its
// accessible label still carries the score.
const scoreSquareBySize: Record<
  ScoreSquareSize,
  { size: string; scoreClassName: string | null; moveClassName: string }
> = {
  masthead: {
    size: "var(--terminal-score-square-size-masthead)",
    scoreClassName: "score-masthead",
    moveClassName: "score-step-masthead",
  },
  row: {
    size: "var(--terminal-score-square-size-row)",
    scoreClassName: "score-row",
    moveClassName: "score-step-row",
  },
  compact: {
    size: "var(--terminal-score-square-size-compact)",
    scoreClassName: null,
    moveClassName: "score-step-row",
  },
}

// The band mark grown to carry the 0–100 score: the band-colored flat square,
// filled, the score centered in the theme's one score ink. An UNCLEAR File has
// no score, so its square shows an en dash. The accessible label names the
// band (or `label`, such as an UNCLEAR reason), so the band is never conveyed
// by hue alone.
export const ScoreSquare = ({
  band,
  score,
  label = dataByAssessmentBand[band].label,
  size = "row",
  className,
}: {
  band: AssessmentBand
  score: number | null
  label?: string
  size?: ScoreSquareSize
  className?: string
}) => {
  const { size: squareSize, scoreClassName } = scoreSquareBySize[size]
  return (
    <span
      role="img"
      aria-label={score === null ? label : `${label}, ${score}`}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-sm font-mono leading-none font-semibold text-(--terminal-score-ink) tabular-nums",
        score === null && "text-(--terminal-score-ink-unclear)",
        scoreClassName,
        className
      )}
      style={{
        backgroundColor: dataByAssessmentBand[band].color,
        width: squareSize,
        height: squareSize,
      }}
    >
      {scoreClassName && (score ?? "–")}
    </span>
  )
}

// The score square with its movement since the previous File: within a band,
// the square then the step (+6, −5) in neutral gray, since the square carries
// all the color; across bands, the before square → the after square at one
// size, with no step.
export const ScoreMovementMark = ({
  band,
  score,
  label = dataByAssessmentBand[band].label,
  movement,
  size = "row",
  className,
}: {
  band: AssessmentBand
  score: number | null
  label?: string
  movement: TerminalScoreMovement | null
  size?: ScoreSquareSize
  className?: string
}) => {
  const { moveClassName } = scoreSquareBySize[size]
  const moveTextClassName = cn(
    "font-mono tabular-nums text-text-secondary",
    moveClassName
  )
  if (movement?.isBandMove)
    return (
      <span className={cn("inline-flex items-center gap-1", className)}>
        <ScoreSquare
          band={movement.previous.band}
          score={movement.previous.score}
          label={`Was ${dataByAssessmentBand[movement.previous.band].label}`}
          size={size}
        />
        <span aria-hidden="true" className={moveTextClassName}>
          →
        </span>
        <ScoreSquare
          band={band}
          score={score}
          label={`now ${label}`}
          size={size}
        />
      </span>
    )
  const delta = movement?.delta
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <ScoreSquare band={band} score={score} label={label} size={size} />
      {!!delta && (
        <span className={moveTextClassName}>
          <span aria-hidden="true">
            {delta > 0 ? "+" : "−"}
            {Math.abs(delta)}
          </span>
          <span className="sr-only">
            {delta > 0 ? "up" : "down"} {Math.abs(delta)} since the last File
          </span>
        </span>
      )}
    </span>
  )
}
